'use client';

import { useState, useEffect, useRef, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Link from 'next/link';
import Image from 'next/image';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Footer } from '../components/Footer';

const COUNTRIES = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'AU', label: 'Australia' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'IE', label: 'Ireland' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'KE', label: 'Kenya' },
  { value: 'NG', label: 'Nigeria' },
  { value: 'GH', label: 'Ghana' },
  { value: 'SG', label: 'Singapore' },
  { value: 'PH', label: 'Philippines' },
  { value: 'KR', label: 'South Korea' },
  { value: 'JP', label: 'Japan' },
  { value: 'BR', label: 'Brazil' },
  { value: 'MX', label: 'Mexico' },
  { value: 'OTHER', label: 'Other' },
];

const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
];

const HOW_KNOW_OPTIONS = [
  { value: 'referral', label: 'Someone referred him to me' },
  { value: 'heard_speak', label: 'I have heard him speak before' },
  { value: 'prior_contact', label: 'I have had prior contact with him' },
  { value: 'other', label: 'Other' },
];

const formSchema = z.object({
  // Contact Information
  name: z.string().min(2, 'Name must be at least 2 characters').max(100),
  title: z.string().min(1, 'Title is required').max(100),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().max(30).optional(),
  organizationName: z.string().min(1, 'Organization name is required').max(200),
  organizationWebsite: z.string().max(200).optional(),
  mailingAddress: z.string().min(1, 'Mailing address is required').max(300),
  country: z.string().min(1, 'Please select a country'),
  state: z.string().optional(),
  isPrimaryContact: z.enum(['yes', 'no'], { required_error: 'Please select an option' }),
  howKnowAndrew: z.string().min(1, 'Please select how you know Andrew'),

  // Event Information
  responseDeadline: z.string().min(1, 'Response deadline is required'),
  eventName: z.string().min(1, 'Event name is required').max(200),
  eventDates: z.string().min(1, 'Event date(s) is required'),
  eventPurpose: z.string().min(1, 'Event purpose is required').max(500),
  eventVenue: z.string().min(1, 'Event venue is required').max(300),
  venueCountry: z.string().min(1, 'Please select venue country'),
  venueState: z.string().optional(),
  yearsHeld: z.string().max(10).optional(),
  sessionTimes: z.string().min(1, 'Session times are required').max(300),
  speakingLength: z.string().min(1, 'Speaking length is required').max(100),
  targetAudience: z.string().min(1, 'Target audience is required').max(200),
  expectedAttendance: z.string().min(1, 'Expected attendance is required').max(50),
  topicRequest: z.string().min(1, 'Topic request is required').max(500),
  eventWebsite: z.string().max(200).optional(),

  // Travel & Logistics
  arrivalAirport: z.string().min(1, 'Arrival airport is required').max(100),
  travelCompanionCoverage: z.enum(['yes', 'no'], { required_error: 'Please select an option' }),
  honorariumBudget: z.string().max(50).optional(),
  publishingPlans: z.string().min(1, 'Publishing plans are required').max(300),
  needsPhotoBio: z.enum(['yes', 'no'], { required_error: 'Please select an option' }),

  // Additional
  additionalDetails: z.string().max(1000).optional(),

  // Honeypot
  website: z.string().max(0).optional(),
});

type FormData = z.infer<typeof formSchema>;

const TEST_DATA_SPEAKING = {
  // Contact Information
  name: 'Jane Test Coordinator',
  title: 'Event Director',
  email: 'jane.test@example.com',
  phone: '+1 555-987-6543',
  organizationName: 'Test Community Church',
  organizationWebsite: 'https://testchurch.example.com',
  mailingAddress: '123 Test Street, Austin, TX 78701',
  country: 'US',
  state: 'TX',
  isPrimaryContact: 'yes' as const,
  howKnowAndrew: 'heard_speak',

  // Event Information
  responseDeadline: '2025-02-01',
  eventName: 'Annual Leadership Summit 2025',
  eventDates: '2025-04-15',
  eventPurpose: 'Our annual leadership summit brings together church leaders from across the region for training, networking, and spiritual renewal.',
  eventVenue: 'Austin Convention Center, 500 E Cesar Chavez St',
  venueCountry: 'US',
  venueState: 'TX',
  yearsHeld: '5',
  sessionTimes: 'Friday 7pm keynote, Saturday 9am & 2pm sessions',
  speakingLength: '45 minutes per session',
  targetAudience: 'Church leaders, pastors, ministry directors',
  expectedAttendance: '300-400',
  topicRequest: 'Building Resilient Leadership Teams',
  eventWebsite: 'https://leadershipsummit.example.com',

  // Travel & Logistics
  arrivalAirport: 'AUS - Austin-Bergstrom International',
  travelCompanionCoverage: 'yes' as const,
  honorariumBudget: '$2,500',
  publishingPlans: 'Livestream on YouTube, podcast distribution after event',
  needsPhotoBio: 'yes' as const,

  // Additional
  additionalDetails: 'We would love for Andrew to join us for dinner with our leadership team the evening before the event.',
};

function SpeakingPageContent() {
  const searchParams = useSearchParams();
  const isTestForm = searchParams.get('testform') === 'true';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);

  // Skip Turnstile on localhost
  const isLocalhost = typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: isTestForm ? TEST_DATA_SPEAKING : undefined,
  });

  // Reset form with test data after mount to handle hydration timing
  useEffect(() => {
    if (isTestForm) {
      reset(TEST_DATA_SPEAKING);
    }
  }, [isTestForm, reset]);

  const onSubmit = async (data: FormData) => {
    // Skip Turnstile check on localhost
    if (!isLocalhost && !turnstileToken) {
      setSubmitStatus({
        type: 'error',
        message: 'Please complete the security verification.',
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('/api/speaking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          turnstileToken: isLocalhost ? 'localhost-bypass' : turnstileToken,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit request');
      }

      setIsSubmitted(true);
      reset();
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    } catch (error: any) {
      setSubmitStatus({
        type: 'error',
        message: error.message || 'Failed to submit request. Please try again later.',
      });
      // Reset turnstile on error so user can try again
      turnstileRef.current?.reset();
      setTurnstileToken(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-background">
      {/* Hero with background image */}
      <div className="relative h-[350px] md:h-[400px] overflow-hidden">
        <Image
          src="/speaking-2.jpg"
          alt="Speaking event background"
          fill
          className="object-cover"
          priority
          sizes="100vw"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/70 via-primary/50 to-primary/80" />

        {/* Back link */}
        <div className="absolute top-0 left-0 right-0 z-10">
          <div className="max-w-5xl mx-auto px-6 lg:px-8 py-6">
            <Link
              href="/"
              className="group inline-flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors"
            >
              <svg
                className="w-4 h-4 transition-transform group-hover:-translate-x-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Home
            </Link>
          </div>
        </div>

        {/* Hero content */}
        <div className="absolute inset-0 flex items-center justify-center text-center px-6">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-sm font-medium tracking-wider uppercase text-white/70 mb-4">
              <span className="w-6 h-px bg-accent/70" />
              Speaking
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white">
              Invite Andrew to Speak
            </h1>
          </div>
        </div>
      </div>

      {/* Form section */}
      <div className="relative z-10 -mt-16 pb-20 lg:pb-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="bg-background border border-border shadow-2xl shadow-primary/5 overflow-hidden">
            {/* Accent bar */}
            <div className="h-1 bg-gradient-to-r from-accent via-accent/80 to-accent/60" />

            <div className="p-6 md:p-10 lg:p-12">
              {isSubmitted ? (
                /* Success Confirmation */
                <div className="text-center py-12 lg:py-20">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-accent/10 mb-8">
                    <svg className="w-10 h-10 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                    Request Submitted
                  </h2>
                  <p className="text-lg text-muted-foreground max-w-md mx-auto mb-8">
                    Thank you for your interest in having Andrew speak at your event. We will review your request and respond shortly.
                  </p>
                  <Link
                    href="/"
                    className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Return to Home
                  </Link>
                </div>
              ) : (
                <>
                  {/* Intro text */}
                  <div className="mb-10 pb-8 border-b border-border/60">
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      Thank you for your interest in having Andrew speak at your event.
                      Please complete the form below with as much detail as possible.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-12">
                {/* Honeypot */}
                <input
                  type="text"
                  {...register('website')}
                  tabIndex={-1}
                  autoComplete="off"
                  style={{ display: 'none' }}
                  aria-hidden="true"
                />

                {/* Section 1: Contact Information */}
                <div className="space-y-6">
                  <h2 className="text-sm font-medium tracking-wider uppercase text-accent flex items-center gap-3">
                    <span className="w-8 h-px bg-accent" />
                    Your Information
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">
                        Your Name <span className="text-accent">*</span>
                      </Label>
                      <Input
                        id="name"
                        {...register('name')}
                        autoComplete="name"
                        className={`h-11 ${errors.name ? 'border-destructive' : ''}`}
                        placeholder="Full name"
                      />
                      {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="title">
                        Your Title <span className="text-accent">*</span>
                      </Label>
                      <Input
                        id="title"
                        {...register('title')}
                        autoComplete="organization-title"
                        className={`h-11 ${errors.title ? 'border-destructive' : ''}`}
                        placeholder="e.g., Pastor, Event Coordinator"
                      />
                      {errors.title && <p className="text-sm text-destructive">{errors.title.message}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="email">
                        Email <span className="text-accent">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        {...register('email')}
                        autoComplete="email"
                        className={`h-11 ${errors.email ? 'border-destructive' : ''}`}
                        placeholder="your@email.com"
                      />
                      {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        {...register('phone')}
                        autoComplete="tel"
                        className="h-11"
                        placeholder="+1 555-123-4567"
                      />
                      <p className="text-xs text-muted-foreground">Include country code for international numbers</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="organizationName">
                        Organization Name <span className="text-accent">*</span>
                      </Label>
                      <Input
                        id="organizationName"
                        {...register('organizationName')}
                        autoComplete="organization"
                        className={`h-11 ${errors.organizationName ? 'border-destructive' : ''}`}
                        placeholder="Church or organization name"
                      />
                      {errors.organizationName && <p className="text-sm text-destructive">{errors.organizationName.message}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="organizationWebsite">Organization Website</Label>
                      <Input
                        id="organizationWebsite"
                        type="url"
                        {...register('organizationWebsite')}
                        autoComplete="url"
                        className="h-11"
                        placeholder="https://..."
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="mailingAddress">
                      Mailing Address <span className="text-accent">*</span>
                    </Label>
                    <Input
                      id="mailingAddress"
                      {...register('mailingAddress')}
                      autoComplete="street-address"
                      className={`h-11 ${errors.mailingAddress ? 'border-destructive' : ''}`}
                      placeholder="Street address, City, ZIP/Postal Code"
                    />
                    {errors.mailingAddress && <p className="text-sm text-destructive">{errors.mailingAddress.message}</p>}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>
                        Country <span className="text-accent">*</span>
                      </Label>
                      <Select key={`country-${isTestForm}`} value={watch('country')} onValueChange={(value) => {
                        setValue('country', value);
                        if (value !== 'US') setValue('state', '');
                      }}>
                        <SelectTrigger className={`h-11 ${errors.country ? 'border-destructive' : ''}`}>
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRIES.map((country) => (
                            <SelectItem key={country.value} value={country.value}>
                              {country.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.country && <p className="text-sm text-destructive">{errors.country.message}</p>}
                    </div>

                    {watch('country') === 'US' && (
                      <div className="space-y-2">
                        <Label>
                          State <span className="text-accent">*</span>
                        </Label>
                        <Select key={`state-${isTestForm}`} value={watch('state')} onValueChange={(value) => setValue('state', value)}>
                          <SelectTrigger className={`h-11 ${errors.state ? 'border-destructive' : ''}`}>
                            <SelectValue placeholder="Select state" />
                          </SelectTrigger>
                          <SelectContent>
                            {US_STATES.map((state) => (
                              <SelectItem key={state.value} value={state.value}>
                                {state.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.state && <p className="text-sm text-destructive">{errors.state.message}</p>}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <Label>
                        Are you the primary contact? <span className="text-accent">*</span>
                      </Label>
                      <RadioGroup
                        value={watch('isPrimaryContact')}
                        onValueChange={(value) => setValue('isPrimaryContact', value as 'yes' | 'no')}
                        className="flex gap-6"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="yes" id="primary-yes" />
                          <Label htmlFor="primary-yes" className="font-normal cursor-pointer">Yes</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="no" id="primary-no" />
                          <Label htmlFor="primary-no" className="font-normal cursor-pointer">No</Label>
                        </div>
                      </RadioGroup>
                      {errors.isPrimaryContact && <p className="text-sm text-destructive">{errors.isPrimaryContact.message}</p>}
                    </div>

                    <div className="space-y-3">
                      <Label>
                        How do you know Andrew? <span className="text-accent">*</span>
                      </Label>
                      <Select key={`howKnowAndrew-${isTestForm}`} value={watch('howKnowAndrew')} onValueChange={(value) => setValue('howKnowAndrew', value)}>
                        <SelectTrigger className={`h-11 ${errors.howKnowAndrew ? 'border-destructive' : ''}`}>
                          <SelectValue placeholder="Select option" />
                        </SelectTrigger>
                        <SelectContent>
                          {HOW_KNOW_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.howKnowAndrew && <p className="text-sm text-destructive">{errors.howKnowAndrew.message}</p>}
                    </div>
                  </div>
                </div>

                {/* Section 2: Event Details */}
                <div className="space-y-6">
                  <h2 className="text-sm font-medium tracking-wider uppercase text-accent flex items-center gap-3">
                    <span className="w-8 h-px bg-accent" />
                    Event Details
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="responseDeadline">
                        Deadline for Response <span className="text-accent">*</span>
                      </Label>
                      <Input
                        id="responseDeadline"
                        type="date"
                        {...register('responseDeadline')}
                        className={`h-11 ${errors.responseDeadline ? 'border-destructive' : ''}`}
                      />
                      {errors.responseDeadline && <p className="text-sm text-destructive">{errors.responseDeadline.message}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="eventName">
                        Name of Event <span className="text-accent">*</span>
                      </Label>
                      <Input
                        id="eventName"
                        {...register('eventName')}
                        className={`h-11 ${errors.eventName ? 'border-destructive' : ''}`}
                        placeholder="Event or conference name"
                      />
                      {errors.eventName && <p className="text-sm text-destructive">{errors.eventName.message}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="eventDates">
                        Date(s) of Event <span className="text-accent">*</span>
                      </Label>
                      <Input
                        id="eventDates"
                        type="date"
                        {...register('eventDates')}
                        className={`h-11 ${errors.eventDates ? 'border-destructive' : ''}`}
                      />
                      {errors.eventDates && <p className="text-sm text-destructive">{errors.eventDates.message}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="yearsHeld">How many years has this event been held?</Label>
                      <Input
                        id="yearsHeld"
                        type="number"
                        min="0"
                        {...register('yearsHeld')}
                        className="h-11"
                        placeholder="e.g., 5"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="eventPurpose">
                      Purpose of Event <span className="text-accent">*</span>
                    </Label>
                    <Textarea
                      id="eventPurpose"
                      {...register('eventPurpose')}
                      className={`resize-none ${errors.eventPurpose ? 'border-destructive' : ''}`}
                      rows={3}
                      placeholder="Describe the purpose and goals of this event..."
                    />
                    {errors.eventPurpose && <p className="text-sm text-destructive">{errors.eventPurpose.message}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="eventVenue">
                      Event Venue & Address <span className="text-accent">*</span>
                    </Label>
                    <Input
                      id="eventVenue"
                      {...register('eventVenue')}
                      className={`h-11 ${errors.eventVenue ? 'border-destructive' : ''}`}
                      placeholder="Venue name and address"
                    />
                    {errors.eventVenue && <p className="text-sm text-destructive">{errors.eventVenue.message}</p>}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>
                        Venue Country <span className="text-accent">*</span>
                      </Label>
                      <Select key={`venueCountry-${isTestForm}`} value={watch('venueCountry')} onValueChange={(value) => {
                        setValue('venueCountry', value);
                        if (value !== 'US') setValue('venueState', '');
                      }}>
                        <SelectTrigger className={`h-11 ${errors.venueCountry ? 'border-destructive' : ''}`}>
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRIES.map((country) => (
                            <SelectItem key={country.value} value={country.value}>
                              {country.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.venueCountry && <p className="text-sm text-destructive">{errors.venueCountry.message}</p>}
                    </div>

                    {watch('venueCountry') === 'US' && (
                      <div className="space-y-2">
                        <Label>
                          Venue State <span className="text-accent">*</span>
                        </Label>
                        <Select key={`venueState-${isTestForm}`} value={watch('venueState')} onValueChange={(value) => setValue('venueState', value)}>
                          <SelectTrigger className={`h-11 ${errors.venueState ? 'border-destructive' : ''}`}>
                            <SelectValue placeholder="Select state" />
                          </SelectTrigger>
                          <SelectContent>
                            {US_STATES.map((state) => (
                              <SelectItem key={state.value} value={state.value}>
                                {state.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.venueState && <p className="text-sm text-destructive">{errors.venueState.message}</p>}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="sessionTimes">
                        Session Times/Dates <span className="text-accent">*</span>
                      </Label>
                      <Input
                        id="sessionTimes"
                        {...register('sessionTimes')}
                        className={`h-11 ${errors.sessionTimes ? 'border-destructive' : ''}`}
                        placeholder="e.g., Friday 7pm, Saturday 9am & 2pm"
                      />
                      {errors.sessionTimes && <p className="text-sm text-destructive">{errors.sessionTimes.message}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="speakingLength">
                        Speaking Length <span className="text-accent">*</span>
                      </Label>
                      <Input
                        id="speakingLength"
                        {...register('speakingLength')}
                        className={`h-11 ${errors.speakingLength ? 'border-destructive' : ''}`}
                        placeholder="e.g., 45 minutes per session"
                      />
                      {errors.speakingLength && <p className="text-sm text-destructive">{errors.speakingLength.message}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="targetAudience">
                        Target Audience <span className="text-accent">*</span>
                      </Label>
                      <Input
                        id="targetAudience"
                        {...register('targetAudience')}
                        className={`h-11 ${errors.targetAudience ? 'border-destructive' : ''}`}
                        placeholder="e.g., Church leaders, Young adults"
                      />
                      {errors.targetAudience && <p className="text-sm text-destructive">{errors.targetAudience.message}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="expectedAttendance">
                        Expected Attendance <span className="text-accent">*</span>
                      </Label>
                      <Input
                        id="expectedAttendance"
                        {...register('expectedAttendance')}
                        className={`h-11 ${errors.expectedAttendance ? 'border-destructive' : ''}`}
                        placeholder="e.g., 200-300"
                      />
                      {errors.expectedAttendance && <p className="text-sm text-destructive">{errors.expectedAttendance.message}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="topicRequest">
                        Topic Request <span className="text-accent">*</span>
                      </Label>
                      <Input
                        id="topicRequest"
                        {...register('topicRequest')}
                        className={`h-11 ${errors.topicRequest ? 'border-destructive' : ''}`}
                        placeholder="Requested topic or theme"
                      />
                      {errors.topicRequest && <p className="text-sm text-destructive">{errors.topicRequest.message}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="eventWebsite">Event Website</Label>
                      <Input
                        id="eventWebsite"
                        type="url"
                        {...register('eventWebsite')}
                        className="h-11"
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                </div>

                {/* Section 3: Travel & Logistics */}
                <div className="space-y-6">
                  <h2 className="text-sm font-medium tracking-wider uppercase text-accent flex items-center gap-3">
                    <span className="w-8 h-px bg-accent" />
                    Travel & Logistics
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="arrivalAirport">
                        Arrival Airport <span className="text-accent">*</span>
                      </Label>
                      <Input
                        id="arrivalAirport"
                        {...register('arrivalAirport')}
                        className={`h-11 ${errors.arrivalAirport ? 'border-destructive' : ''}`}
                        placeholder="Nearest airport code or name"
                      />
                      {errors.arrivalAirport && <p className="text-sm text-destructive">{errors.arrivalAirport.message}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="honorariumBudget">Honorarium Budget</Label>
                      <Input
                        id="honorariumBudget"
                        {...register('honorariumBudget')}
                        className="h-11"
                        placeholder="$"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <Label>
                        Will you cover travel for a companion? <span className="text-accent">*</span>
                      </Label>
                      <RadioGroup
                        value={watch('travelCompanionCoverage')}
                        onValueChange={(value) => setValue('travelCompanionCoverage', value as 'yes' | 'no')}
                        className="flex gap-6"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="yes" id="companion-yes" />
                          <Label htmlFor="companion-yes" className="font-normal cursor-pointer">Yes</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="no" id="companion-no" />
                          <Label htmlFor="companion-no" className="font-normal cursor-pointer">No</Label>
                        </div>
                      </RadioGroup>
                      {errors.travelCompanionCoverage && <p className="text-sm text-destructive">{errors.travelCompanionCoverage.message}</p>}
                    </div>

                    <div className="space-y-3">
                      <Label>
                        Do you need a photograph & bio? <span className="text-accent">*</span>
                      </Label>
                      <RadioGroup
                        value={watch('needsPhotoBio')}
                        onValueChange={(value) => setValue('needsPhotoBio', value as 'yes' | 'no')}
                        className="flex gap-6"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="yes" id="photo-yes" />
                          <Label htmlFor="photo-yes" className="font-normal cursor-pointer">Yes</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="no" id="photo-no" />
                          <Label htmlFor="photo-no" className="font-normal cursor-pointer">No</Label>
                        </div>
                      </RadioGroup>
                      {errors.needsPhotoBio && <p className="text-sm text-destructive">{errors.needsPhotoBio.message}</p>}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="publishingPlans">
                      Publishing Plans <span className="text-accent">*</span>
                    </Label>
                    <Input
                      id="publishingPlans"
                      {...register('publishingPlans')}
                      className={`h-11 ${errors.publishingPlans ? 'border-destructive' : ''}`}
                      placeholder="How will audio/video be distributed? (e.g., livestream, podcast)"
                    />
                    {errors.publishingPlans && <p className="text-sm text-destructive">{errors.publishingPlans.message}</p>}
                  </div>
                </div>

                {/* Section 4: Additional Details */}
                <div className="space-y-6">
                  <h2 className="text-sm font-medium tracking-wider uppercase text-accent flex items-center gap-3">
                    <span className="w-8 h-px bg-accent" />
                    Additional Information
                  </h2>

                  <div className="space-y-2">
                    <Label htmlFor="additionalDetails">Anything else we should know?</Label>
                    <Textarea
                      id="additionalDetails"
                      {...register('additionalDetails')}
                      className="resize-none"
                      rows={4}
                      placeholder="Any additional details about your event or special requests..."
                    />
                  </div>
                </div>

                {/* Turnstile Widget - skip on localhost */}
                {!isLocalhost && (
                  <div className="flex justify-center">
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || ''}
                      onSuccess={setTurnstileToken}
                      onError={() => setTurnstileToken(null)}
                      onExpire={() => setTurnstileToken(null)}
                    />
                  </div>
                )}

                {/* Status Messages */}
                {submitStatus && (
                  <Alert
                    variant={submitStatus.type === 'error' ? 'destructive' : 'default'}
                    className={
                      submitStatus.type === 'success'
                        ? 'bg-accent/10 text-foreground border-accent/30'
                        : ''
                    }
                  >
                    <AlertDescription className="font-medium flex items-center gap-2">
                      {submitStatus.type === 'success' && (
                        <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      {submitStatus.message}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full h-14 text-base font-medium bg-primary hover:bg-primary/90 transition-all duration-300 hover:shadow-lg hover:shadow-primary/20"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Submitting...
                    </span>
                  ) : (
                    'Submit Speaking Request'
                  )}
                </Button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
}

export default function SpeakingPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-background" />}>
      <SpeakingPageContent />
    </Suspense>
  );
}
